import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { StoreData } from '@/templates/TemplateInterface';
import { SimpleTemplate } from '@/templates/simple/SimpleTemplate';
import { PREVIEW_MESSAGE, PREVIEW_READY, isPreviewFrame, type SalesLinkDraft } from '@/lib/sales-link';
import { trackSalesEvent } from '@/lib/sales-events';
import { StorefrontPageLoader } from '@/components/storefront/StorefrontLoaders';
import { CustomerAuthProvider } from '@/contexts/CustomerAuthContext';
import { EnhancedCartProvider } from '@/contexts/EnhancedCartContext';
import { applyStorefrontHead, storeCanonicalUrl, stripHtml } from '@/lib/storefrontHead';

export function StorefrontPage() {
  const { storeUrl } = useParams<{ storeUrl: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [store, setStore] = useState<StoreData | null>(null);
  const [previewDraft, setPreviewDraft] = useState<SalesLinkDraft | null>(null);
  const preview = isPreviewFrame();

  useEffect(() => {
    const fetchStore = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await api.get(`/api/public/stores/${storeUrl}`);
        const data = response.data;

        // Normalize API payload to match TemplateInterface expectations
        const normalized: StoreData = {
          business: data.business,
          products: (data.products || []).map((p: any) => ({
            id: p._id || p.id,
            name: p.name,
            description: p.description,
            price: typeof p.basePrice === 'number' ? p.basePrice : (typeof p.price === 'number' ? p.price : 0),
            compareAtPrice: typeof p.compareAtPrice === 'number' ? p.compareAtPrice : undefined,
            images: Array.isArray(p.images) ? p.images.map((img: any) => ({ url: img.url, alt: img.alt })) : [],
            category: typeof p.category === 'object' && p.category ? p.category.name : p.category,
            tags: p.tags,
            options: p.options,
            wholesaleEnabled: Boolean(p.wholesaleEnabled),
            wholesaleMinQuantity: typeof p.wholesaleMinQuantity === 'number' ? p.wholesaleMinQuantity : undefined,
            wholesaleTierPrice: typeof p.wholesaleTierPrice === 'number' ? p.wholesaleTierPrice : undefined,
            variants: Array.isArray(p.variants) ? p.variants.map((v: any) => ({
              sku: v.sku,
              options: v.options instanceof Map ? Object.fromEntries(v.options) : (v.options || {}),
              price: v.price,
              compareAtPrice: v.compareAtPrice,
              inventory: v.inventory,
              wholesaleMinQuantity: typeof v.wholesaleMinQuantity === 'number' ? v.wholesaleMinQuantity : undefined,
              wholesaleTierPrice: typeof v.wholesaleTierPrice === 'number' ? v.wholesaleTierPrice : undefined,
            })) : undefined,
            status: p.status || 'active',
          })),
          services: (data.services || []).map((s: any) => ({
            id: s._id || s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            duration: s.duration,
            image: s.image,
            category: s.category,
            status: s.status || 'active',
          })),
          bookingSlots: (data.bookingSlots || []).map((slot: any) => ({
            _id: slot._id,
            id: slot._id,
            name: slot.name,
            description: slot.description,
            price: slot.price,
            compareAtPrice: slot.compareAtPrice,
            images: Array.isArray(slot.images) ? slot.images.map((img: any) => ({ url: img.url, alt: img.alt })) : [],
            category: slot.category,
            bookingType: slot.bookingType,
            duration: slot.duration,
            timeInterval: slot.timeInterval || 30,
            capacity: slot.capacity,
            status: slot.status || 'active',
            availability: slot.availability || undefined,
          })),
          bookingSettings: data.bookingSettings || { enabled: false, disableProducts: false },
        };

        setStore(normalized);
      } catch (err: any) {
        console.error('Error fetching store:', err);
        setError(err.response?.data?.message || 'Store not found');
        
        // If store not found, navigate to homepage after a delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    if (storeUrl) {
      fetchStore();
    }
  }, [storeUrl, navigate]);

  // Imperatively update document head for storefront (Helmet appears unreliable here)
  useEffect(() => {
    if (typeof document === 'undefined' || !store) return;

    const storeName = store.business.storeName || store.business.name;
    const seo = store.business.seo || {};
    const title = stripHtml(seo.title) || storeName;
    const description =
      stripHtml(seo.description) ||
      stripHtml(store.business.businessDescription) ||
      `Shop online at ${storeName}. Browse our collection of products and find what you're looking for.`;
    const keywords =
      seo.keywords || `${storeName}, online store, shop, ecommerce`;
    const ogImage = seo.ogImage || store.business.settings.logo || '';
    const logo = store.business.settings.logo || '';
    const canonicalUrl = storeCanonicalUrl(store.business.url);
    const address = store.business.address;
    const sameAs = store.business.socialLinks
      ? Object.values(store.business.socialLinks).filter(
          (value) => typeof value === 'string' && /^https?:\/\//i.test(value)
        )
      : [];

    applyStorefrontHead({
      title,
      description,
      keywords,
      author: storeName,
      canonicalUrl,
      ogImage,
      logo,
      siteName: storeName,
      type: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': ['Store', 'OnlineStore'],
        name: storeName,
        description,
        url: canonicalUrl,
        image: ogImage || undefined,
        logo: logo || undefined,
        telephone: store.business.whatsappNumber || undefined,
        email: store.business.email || undefined,
        address: address
          ? {
              '@type': 'PostalAddress',
              streetAddress: address.street || undefined,
              addressLocality: address.city || undefined,
              addressRegion: address.state || undefined,
              postalCode: address.postalCode || undefined,
              addressCountry: address.country || undefined,
            }
          : undefined,
        sameAs,
      },
    });
  }, [store]);

  useEffect(() => {
    if (store?.business.url && !preview) trackSalesEvent(store.business.url, 'visit');
  }, [store?.business.url, preview]);

  useEffect(() => {
    if (!preview) return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== window.parent) return;
      if (event.data?.type !== PREVIEW_MESSAGE) return;
      const draft = event.data.draft as SalesLinkDraft | undefined;
      if (!draft?.colors || !draft.layoutOptions || !draft.hero) return;
      setPreviewDraft(draft);
    };
    window.addEventListener('message', onMessage);
    window.parent?.postMessage({ type: PREVIEW_READY }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  }, [preview]);

  const displayStore = useMemo(() => {
    if (!store || !previewDraft) return store;
    return {
      ...store,
      business: {
        ...store.business,
        settings: {
          ...store.business.settings,
          colors: {
            ...store.business.settings.colors,
            ...previewDraft.colors,
          },
          customization: {
            ...store.business.settings.customization,
            template: previewDraft.templateId === 'classic' ? 'simple' : previewDraft.templateId,
            hero: previewDraft.hero,
            banner: {
              enabled: previewDraft.banner.enabled,
              imageUrl: previewDraft.banner.imageUrl,
            },
            announcements: previewDraft.announcements,
            layoutOptions: previewDraft.layoutOptions,
            salesLink: { activeTemplateId: previewDraft.templateId, templates: [] },
          },
        },
      },
    };
  }, [store, previewDraft]);

  if (isLoading) {
    return <StorefrontPageLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center px-4">
          <h1 className="text-3xl font-bold text-destructive">Store Not Found</h1>
          <p className="text-muted-foreground">
            The store you're looking for doesn't exist or has been moved.
          </p>
          <p className="text-sm text-muted-foreground">
            Redirecting to homepage...
          </p>
        </div>
      </div>
    );
  }

  if (!displayStore) {
    return null;
  }

  const theme = {
    primaryColor: displayStore.business.settings.colors?.primary,
    secondaryColor: displayStore.business.settings.colors?.secondary,
  };

  return (
    <CustomerAuthProvider storeUrl={storeUrl || ''}>
      <EnhancedCartProvider storeUrl={preview ? `preview_${storeUrl}` : storeUrl}>
        <SimpleTemplate store={displayStore} theme={theme} />
      </EnhancedCartProvider>
    </CustomerAuthProvider>
  );
} 